/* eslint-disable simple-import-sort/imports */
import { useCallback, useRef, useState } from 'react';
import { Logger } from '@saviynt/common';

import type { NestedListRowData } from '@saviynt/design-system';
import type { RuleAction } from '../../../../services';
import type { ActiveFilters } from '../helpers/filterUtils';
import type {
  EntitlementRow,
  GroupedEntitlement,
} from '../IncludedEntitlements';

import UserService from '../../../../services/UserService/UserService';
import { getHasAccessParam } from '../../../../shared/utils/filterUtils';

// Temporary extension for fields not yet in API response
// Remove this when API includes entitlementGlossary and entitlementPrivileged
interface ExtendedEntitlementMetaData {
  entitlementGlossary?: string;
  entitlementPrivileged?: string;
  securitySystem?: {
    securitySystemName?: string;
    securitySystemDisplayName?: string;
  };
}

interface UseEntitlementExpansionParams {
  actions: RuleAction[];
  ruleData:
    | {
        userMatchCount: number;
        confidence: number;
        entitlements: number;
        conditions?: {
          objectName: string;
          operation: string;
          objectValue: string | string[];
        }[];
      }
    | undefined;
  activeFilters: ActiveFilters;
}

interface UseEntitlementExpansionReturn {
  groupedEntitlements: GroupedEntitlement[];
  setGroupedEntitlements: React.Dispatch<
    React.SetStateAction<GroupedEntitlement[]>
  >;
  expandedEntitlements: Set<string>;
  setExpandedEntitlements: React.Dispatch<React.SetStateAction<Set<string>>>;
  handleExpandToggle: (row: { data: EntitlementRow }) => Promise<void>;
  loadMoreUsers: (endpointName: string) => Promise<void>;
  clearAllUserData: () => void;
  handleCollapseAllGroups: () => void;
  syncExpandedEntitlementsWithRowsData: (rowsData: NestedListRowData[]) => void;
}

/**
 * Custom hook for managing entitlement expansion state and logic
 * Handles user data fetching, pagination, and expansion state management
 */
// eslint-disable-next-line import/prefer-default-export
export const useEntitlementExpansion = ({
  actions,
  ruleData,
  activeFilters,
}: UseEntitlementExpansionParams): UseEntitlementExpansionReturn => {
  // Initialize grouped entitlements from actions and rule data
  const [groupedEntitlements, setGroupedEntitlements] = useState<
    GroupedEntitlement[]
  >(() => {
    if (!ruleData) {
      return [];
    }

    const grouped = new Map<string, GroupedEntitlement>();

    actions.forEach((action: RuleAction) => {
      const {
        endpoint,
        evKey,
        entitlementValueName,
        evDisplayName,
        entitlementType,
      } = action.entitlementMetaData;

      // Temp cast to extended type to safely access fields that may not exist yet
      // (entitlementGlossary and entitlementPrivileged are not yet in the API response)
      const extendedMetaData =
        action.entitlementMetaData as ExtendedEntitlementMetaData &
          RuleAction['entitlementMetaData'];

      const key = endpoint.endpointName;

      if (!grouped.has(key)) {
        grouped.set(key, {
          endpointName: endpoint.endpointName,
          endpointDisplayName: endpoint.endpointDisplayName,
          endpointDescription: endpoint.description ?? '',
          logo: endpoint.logo,
          confidence: ruleData.confidence,
          totalUserCount: ruleData.userMatchCount,
          entitlements: [],
          users: [], // will be populated when expanded
          isLoadingUsers: false,
          // Initialize pagination fields
          currentPage: 0,
          hasMoreUsers: false, // set based on API data after first load
          isLoadingMoreUsers: false,

          // Extract additional fields for entitlement side panel display
          entitlementGlossary: extendedMetaData.entitlementGlossary,
          entitlementPrivileged: extendedMetaData.entitlementPrivileged
            ? String(extendedMetaData.entitlementPrivileged)
            : undefined,
          securitySystemName:
            extendedMetaData.securitySystem?.securitySystemName,
          securitySystemDisplayName:
            extendedMetaData.securitySystem?.securitySystemDisplayName,
        });
      }

      const group = grouped.get(key);

      if (group) {
        group.entitlements.push({
          evKey,
          entitlementValueName,
          evDisplayName,
          evDescription: action.entitlementMetaData.evDescription ?? null,
          entitlementTypeName: entitlementType.entitlementTypeName,
          entitlementDisplayName: entitlementType.entitlementDisplayName, // from API: entitlementMetaData.entitlementType.entitlementDisplayName
          entitlementTypeDescription: entitlementType.description ?? '', // from API: entitlementMetaData.entitlementType.description
          existingUserCount: action.entitlementMetaData.existingUserCount,
          trustScore: action.entitlementMetaData.trustScore,
        });
      }
    });

    return Array.from(grouped.values());
  });

  // Track which entitlements are expanded by evKey
  const [expandedEntitlements, setExpandedEntitlements] = useState<Set<string>>(
    new Set()
  );

  // Flag to track when we're making internal updates (to prevent infinite reconciliation loops)
  // This prevents reconciliation when WE change the state (collapse all, filters, etc.)
  const isInternalUpdate = useRef(false);

  // Function to clear all cached user data when filter changes
  const clearAllUserData = useCallback(() => {
    setGroupedEntitlements((prev) =>
      prev.map((group) => ({
        ...group,
        users: [], // Clear cached users
        isLoadingUsers: false,
        currentPage: 0,
        hasMoreUsers: false,
      }))
    );
  }, []);

  // Function to collapse all groups
  const handleCollapseAllGroups = useCallback(() => {
    // Flag that this is an internal update to prevent reconciliation loop
    isInternalUpdate.current = true;
    setExpandedEntitlements(new Set());
  }, []);

  // Function to fetch users when a row is expanded
  const handleExpandToggle = useCallback(
    async (row: { data: EntitlementRow }) => {
      const entitlement = row.data;
      const evKeyString = entitlement.evKey.toString();

      // Toggle this entitlement's expansion state
      // Flag that this is an internal update to prevent reconciliation loop
      isInternalUpdate.current = true;
      setExpandedEntitlements((prev) => {
        const newSet = new Set(prev);

        if (newSet.has(evKeyString)) {
          newSet.delete(evKeyString);
        } else {
          newSet.add(evKeyString);
        }

        return newSet;
      });

      // Only fetch if no users loaded yet and not currently loading
      if (entitlement.users.length === 0 && !entitlement.isLoadingUsers) {
        try {
          // Set loading state
          setGroupedEntitlements((prev) =>
            prev.map((g) =>
              g.endpointName === entitlement.endpointName
                ? { ...g, isLoadingUsers: true }
                : g
            )
          );

          const hasAccessParam = getHasAccessParam(activeFilters.access_status);

          const requestBody = {
            conditions: ruleData?.conditions ?? [],
            columns: [
              'userkey',
              'firstname',
              'lastname',
              'username',
              'country',
              'city',
              'costcenter',
              'departmentname',
              'owner',
              'employeetype',
              'title',
              'manager',
            ],
            entitlementValueKey: String(entitlement.evKey),
            pagination: { max: 5, page: 1 },
            ...(hasAccessParam !== undefined && { hasAccess: hasAccessParam }),
          };

          const { users, pagination } = await UserService.getUsers(requestBody);

          // Update with fetched users - this will trigger useMemo to re-compute rowsData
          setGroupedEntitlements((prev) =>
            prev.map((g) =>
              g.endpointName === entitlement.endpointName
                ? {
                    ...g,
                    users,
                    isLoadingUsers: false,
                    currentPage: 1, // First page loaded
                    hasMoreUsers: Boolean(pagination.nextPage),
                  }
                : g
            )
          );
        } catch (error) {
          Logger.error('Failed to fetch users', error);
          // Handle error state
          setGroupedEntitlements((prev) =>
            prev.map((g) =>
              g.endpointName === entitlement.endpointName
                ? {
                    ...g,
                    isLoadingUsers: false,
                    hasMoreUsers: false, // Don't show load more if initial load failed
                  }
                : g
            )
          );
        }
      } else {
        // skip fetch - users already loaded or loading for this group
      }
    },
    [ruleData, activeFilters.access_status]
  );

  // Function to load more users for a specific group
  const loadMoreUsers = useCallback(
    async (endpointName: string) => {
      const group = groupedEntitlements.find(
        (g) => g.endpointName === endpointName
      );

      if (!group || group.isLoadingMoreUsers || !group.hasMoreUsers) {
        return;
      }

      try {
        // Set loading state for this specific group
        setGroupedEntitlements((prev) =>
          prev.map((g) =>
            g.endpointName === endpointName
              ? { ...g, isLoadingMoreUsers: true }
              : g
          )
        );

        const hasAccessParam = getHasAccessParam(activeFilters.access_status);
        const nextPage = group.currentPage + 1;
        const requestBody = {
          conditions: ruleData?.conditions ?? [],
          columns: [
            'userkey',
            'firstname',
            'lastname',
            'username',
            'country',
            'city',
            'costcenter',
            'departmentname',
            'owner',
            'employeetype',
            'title',
            'manager',
          ],
          entitlementValueKey: String(group.entitlements[0]?.evKey),
          pagination: { max: 5, page: nextPage },
          ...(hasAccessParam !== undefined && { hasAccess: hasAccessParam }),
        };

        const { users: newUsers, pagination } = await UserService.getUsers(
          requestBody
        );

        const hasMoreUsers = Boolean(pagination.nextPage);

        // Append new users to existing users
        setGroupedEntitlements((prev) =>
          prev.map((g) =>
            g.endpointName === endpointName
              ? {
                  ...g,
                  users: [...g.users, ...newUsers],
                  currentPage: nextPage,
                  hasMoreUsers,
                  isLoadingMoreUsers: false,
                }
              : g
          )
        );
      } catch (error) {
        Logger.error('Failed to load more users', error);
        // Handle error state
        setGroupedEntitlements((prev) =>
          prev.map((g) =>
            g.endpointName === endpointName
              ? { ...g, isLoadingMoreUsers: false }
              : g
          )
        );
      }
    },
    [groupedEntitlements, ruleData, activeFilters.access_status]
  );

  // State Reconciliation: Sync our expandedEntitlements with DS component's actual state
  // This fixes the bug where DS component handles collapse internally without calling our handler
  const syncExpandedEntitlementsWithRowsData = useCallback(
    (rowsData: NestedListRowData[]) => {
      // Skip reconciliation if we just made an internal update (prevents infinite loops)
      if (isInternalUpdate.current) {
        isInternalUpdate.current = false; // Reset the flag

        return;
      }

      // Extract which entitlements are actually expanded according to the DS component
      const actualExpandedEntitlements = new Set<string>();

      rowsData.forEach((row: NestedListRowData) => {
        if (row.isNestedRowExpanded && row.data) {
          // Use evKey from the EntitlementRow data
          const entitlement = row.data as EntitlementRow;

          actualExpandedEntitlements.add(entitlement.evKey.toString());
        }
      });

      // Helper function to check if two Sets contain the same values
      const setsAreEqual = (set1: Set<string>, set2: Set<string>): boolean => {
        if (set1.size !== set2.size) {
          return false;
        }

        // Use Array.from to avoid for...of loop
        const set1Array = Array.from(set1);

        return set1Array.every((item) => set2.has(item));
      };

      // Only update our state if there's actually a difference
      // This prevents infinite loops and unnecessary re-renders
      if (!setsAreEqual(expandedEntitlements, actualExpandedEntitlements)) {
        setExpandedEntitlements(actualExpandedEntitlements);
      }
    },
    [expandedEntitlements]
  );

  return {
    groupedEntitlements,
    setGroupedEntitlements,
    expandedEntitlements,
    setExpandedEntitlements,
    handleExpandToggle,
    loadMoreUsers,
    clearAllUserData,
    handleCollapseAllGroups,
    syncExpandedEntitlementsWithRowsData,
  };
};
